<template>
  <div class="cart-wrapper">


    <div v-if="parseFloat(cart.locker_free_shipping_threshold) > 0" class="info-text  info-text--gray mb-4">
      <span v-html="in18['page_cart_free_delivery_msg_1']"/>
      <b>&nbsp;{{ cart.locker_free_shipping_threshold }}&nbsp;</b>
      <span v-html="in18['page_cart_free_delivery_msg_2']"/>
    </div>


    <div v-if="!cart.available_for_purchase" class="info-text  info-text--red mb-4">
      <span v-html="cart.min_cart_price_message"/>
    </div>

    <table class="cart">
      <thead>
      <tr>
        <!-- Header row with checkboxes and column names -->
        <th>
          <div class="checkbox">
            <!-- Select all/deselect all checkbox -->
            <input
                id="check_all"
                v-model="selectAll"
                class="checkbox__input"
                name="check_all"
                type="checkbox"
                @change="toggleSelectAll"
            >
            <label class="checkbox__label" for="check_all"></label>
          </div>
        </th>
        <th>{{ in18['page_cart_item_product'] }}</th>
        <th>{{ in18['page_cart_item_quantity'] }}</th>
        <th>{{ in18['page_cart_item_price'] }}</th>
      </tr>
      </thead>
      <tbody>
      <template v-for="item in cart.items">
        <template v-if="item.product.type === 'product'">
          <tr>
            <!-- Cart item row -->
            <td>
              <div class="checkbox">
                <!-- Individual item checkbox -->
                <input
                    :id="'check_' + item.id"
                    :checked="selectedItems.includes(item.id)"
                    :name="'check_' + item.id"
                    class="checkbox__input"
                    type="checkbox"
                    @change="toggleItemSelection(item.id)"
                >
                <label :for="'check_' + item.id" class="checkbox__label"></label>
              </div>
            </td>
            <td>
              <div class="cart__prod-info">
                <!-- Product information -->
                <div class="cart__prod-photo">
                  <a :href="item.product.link" :title="item.product.title">
                    <picture>
                      <source :srcset="item.product.image.webp" type="image/webp">
                      <img :alt="item.product.title" :src="item.product.image.source" loading="lazy">
                    </picture>
                  </a>
                </div>
                <div class="cart__prod-data">
                  <div class="cart__prod-title">
                    <a :href="item.product.link" :title="item.product.title">
                      {{ item.product.title }}
                    </a>
                  </div>
                  <ul class="product__data  product__data--sm">
                    <li class="product_data-item">
                      {{ item.product.manufacture.title }}
                    </li>
                    <li class="product_data-item">
                      {{ item.product.code }}
                    </li>
                  </ul>
                </div>
              </div>
            </td>
            <td>
              <div class="counter">
                <!-- Quantity controls -->
                <button class="counter__btn minus" type="button"
                        @click="decrementQuantity(item)"
                        :disabled="inProgress"
                >
                  - <!-- Decrement button -->
                </button>
                <input
                    v-model="item.quantity"
                    class="counter__input"
                    type="text"
                    @blur="validateQuantity(item)"
                    @input="updateQuantity(item)"
                >
                <button class="counter__btn plus" type="button"
                        @click="incrementQuantity(item)"
                        :disabled="inProgress"
                >
                  + <!-- Increment button -->
                </button>
              </div>
              <div class="mt-2">
                <!-- Remove item button -->
                <button
                    :title="in18['page_cart_item_remove_btn']"
                    class="btn btn--reset fw-400"
                    type="button"
                    @click="removeItem(item)"
                >
                  {{ in18['page_cart_item_remove_btn'] }}
                </button>
              </div>
            </td>
            <td>
              <!-- Display old price with discount if applicable -->
              <del v-if="item.coupon_total_price_with_tax > 0" class="old-price">
                <b class="color-gray">{{ item.formatted_old_price_with_tax }}</b>
              </del>
              <!-- Item total price -->
              <div>
                <b>{{ item.formatted_total_price_with_tax }}</b>
              </div>
            </td>
          </tr>
          <tr v-if="item.is_delayed_delivery" class="shipping_msg">
            <td colspan="4">
              <div class="info-text  info-text--gray">
                <span v-html="in18['page_cart_delayed_delivery_msg_1']"/>
                <b> "{{ item.product.title }}"</b>,
                <span v-html="in18['page_cart_delayed_delivery_msg_2']"/>
              </div>
            </td>
          </tr>
        </template>
      </template>
      </tbody>
    </table>
    <div class="text-right">
      <!-- Button to perform a cart action -->
      <button
          :title="in18['page_cart_item_remove_btn']"
          class="btn btn--reset fw-400"
          type="button"
          @click="deleteSelectedItems"
      >
        {{ selectedItems.length > 0 ? in18['page_cart_item_remove_selected_btn'] : in18['page_cart_item_remove_btn'] }}
      </button>
    </div>
  </div>
</template>

<script>

export default {

  props: {
    cart: {
      type: Object
    },
    in18: {
      type: Object
    },
  },

  data() {
    return {
      inProgress:    false,
      selectAll:     false, // Track whether all checkboxes are selected
      selectedItems: [], // Array to store selected items
    };
  },
  emits: [
    'update-cart'
  ],

  created() {
    this.debouncedUpdateCartItem = this.debounce(this.updateCartItem, 500);
  },

  computed: {
    // Compute selected items based on selectedItems array
    selectedCartItems() {
      return this.cart.items.filter((item) => this.selectedItems.includes(item.id));
    },
  },

  methods: {

    // Limiting rapid changes to the product quantity counter
    debounce(func, delay) {
      let timer;
      return function (...args) {
        clearTimeout(timer);
        timer = setTimeout(() => func.apply(this, args), delay);
      };
    },

    // Handle the quantity update when the input field changes
    // You may want to add validation here as well
    updateQuantity(item) {
      if (item.quantity < 1) {
        item.quantity = 1; // Ensure a minimum quantity of 1
      }
    },

    // Decrement the quantity by 1
    decrementQuantity(item) {
      if (item.quantity > 1) {
        item.quantity--;
        this.debouncedUpdateCartItem(item, item.quantity);
        ga_remove_from_cart_event(this.cart, item, 1);
      }
    },

    // Increment the quantity by 1
    incrementQuantity(item) {
      item.quantity++;
      this.debouncedUpdateCartItem(item, item.quantity);
      ga_add_to_cart_event(this.cart, item, 1);
    },

    // Perform any additional validation if needed when the input loses focus
    validateQuantity(item) {
      if (item.quantity < 1) {
        item.quantity = 1; // Ensure a minimum quantity of 1
      }
      this.updateCartItem(item, item.quantity);
    },

    // Toggle select all or deselect all
    toggleSelectAll() {
      if (this.selectAll) {
        this.selectedItems = this.cart.items.map((item) => item.id);
      } else {
        this.selectedItems = [];
      }
    },

    // Toggle individual item's selection
    toggleItemSelection(itemId) {
      if (this.selectedItems.includes(itemId)) {
        // Item is currently selected, remove it from selectedItems
        this.selectedItems = this.selectedItems.filter((id) => id !== itemId);
      } else {
        // Item is not selected, add it to selectedItems
        this.selectedItems.push(itemId);
      }
    },
// Handle item removal
    removeItem(item) {
      if (confirm(this.in18['page_remove_item_confirm']) !== true) {
        return;
      }

      if (this.inProgress) {
        return;
      }

      this.inProgress = true;

      axios.post('/api/cart-item/' + item.id + '/delete')
          .then((response) => {
            window.toastr.success(response.data.message);
            this.$emit('update-cart', response.data.cart);
            $('#cart-items-count').text(response.data.cart.total_quantity);

            ga_remove_from_cart_event(
              response.data.cart,
              response.data.cart.items.find(item => item.product.id === item.product.id)
            );
          })
          .catch((error) => {
            // this.$emit('update-cart', error.response.data.errors.cart);
            window.toastr.error(error.response.data.message);
          })
          .finally(() => {
            this.inProgress = false;
          });
    },

    // Delete selected items
    deleteSelectedItems() {

      if (confirm(this.in18['page_remove_item_confirm']) !== true) {
        return;
      }

      if (this.inProgress) {
        return;
      }

      this.inProgress = true;

      let data = this.selectedCartItems.length > 0 ? this.selectedCartItems : this.cart.items;

      // Loop through selected items and delete them
      Promise.all(
          data.map((item) =>
              axios.post('/api/cart-item/' + item.id + '/delete')
          )
      )
          .then((responses) => {

            responses.forEach((response) => {
              window.toastr.success(response.data.message);
            });

            // Update the cart after deleting items
            const updatedCart = responses[responses.length - 1].data.cart;
            this.$emit('update-cart', updatedCart);
            $('#cart-items-count').text(updatedCart.total_quantity);

            ga_remove_from_cart_event(
              updatedCart,
              updatedCart.items.find(item => item.product.id === item.product.id)
            );

            // Deselect all after deletion
            this.selectedItems = [];
          })
          .catch((error) => {
            window.toastr.error(error.response.data.message);
          })
          .finally(() => {
            this.inProgress = false;
          });
    },

    // Update the cart item's quantity
    updateCartItem(item, quantity) {

      if (this.inProgress) {
        return;
      }

      this.inProgress = true;

      axios.post('/api/cart-item/add/' + item.product.id, {
        'quantity': quantity
      })
          .then((response) => {
            window.toastr.success(response.data.message);
            this.$emit('update-cart', response.data.cart);
            $('#cart-items-count').text(response.data.cart.total_quantity);
          })
          .catch((error) => {
            this.$emit('update-cart', error.response.data.errors.cart);
            window.toastr.error(error.response.data.message);
          })
          .finally(() => {
            this.inProgress = false;
          });
    },
  },
};
</script>

<style>
.info-text--red {
  background-color: #ff525f;
  color: white;
}
</style>
